import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CouponModal } from './CouponModal';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { SAFinanceiroSkeleton } from './SASkeletons';
import { toast } from '@/hooks/use-toast';

const monoFont = "'IBM Plex Mono', monospace";

interface Coupon {
  id: string; code: string; description: string | null; discount_type: string;
  discount_value: number; applicable_plans: string[]; max_uses: number | null;
  uses_count: number; expires_at: string | null; is_active: boolean; created_at: string;
}

function formatCurrency(cents: number) { return `R$ ${(cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`; }

export function SAFinanceiro() {
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<Coupon | null>(null);
  const [planBreakdown, setPlanBreakdown] = useState<{ plan: string; count: number; mrr: number }[]>([]);
  const [mrrCents, setMrrCents] = useState(0);
  const [churn, setChurn] = useState(0);

  const load = async () => {
    // Metrics
    await supabase.rpc('refresh_admin_metrics');
    const { data: m } = await supabase.from('admin_metrics_cache').select('*').limit(1).maybeSingle();
    if (m) setMrrCents((m as any).mrr_cents || 0);

    // Plan breakdown
    const { data: profiles } = await supabase.from('user_profiles').select('plan, status').eq('status', 'active');
    if (profiles) {
      const counts: Record<string, number> = {};
      (profiles as any[]).forEach(p => { counts[p.plan] = (counts[p.plan] || 0) + 1; });
      const prices: Record<string, number> = { free: 0, pro: 8900, custom: 15000 };
      setPlanBreakdown(Object.entries(counts).map(([plan, count]) => ({ plan, count, mrr: count * (prices[plan] || 0) })));
    }

    // Churn
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
    const { count: cancelled30d } = await supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('status', 'cancelled').gte('updated_at', thirtyDaysAgo);
    const prevActive = (m as any)?.prev_active_users || 1;
    setChurn(Math.round(((cancelled30d || 0) / prevActive) * 100));

    // Coupons
    const { data: c } = await supabase.from('coupons').select('*').order('created_at', { ascending: false });
    setCoupons((c as any[]) || []);

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleToggleCoupon = async () => {
    if (!toggleTarget) return;
    await supabase.from('coupons').update({ is_active: !toggleTarget.is_active } as any).eq('id', toggleTarget.id);
    setToggleTarget(null); load();
    toast({ title: toggleTarget.is_active ? 'Cupom desativado' : 'Cupom reativado' });
  };

  if (loading) return <SAFinanceiroSkeleton />;

  const arrCents = mrrCents * 12;
  const maxPlanCount = Math.max(...planBreakdown.map(p => p.count), 1);

  const kpis = [
    { label: 'MRR', value: formatCurrency(mrrCents) },
    { label: 'ARR PROJETADO', value: formatCurrency(arrCents) },
    { label: 'CHURN MENSAL', value: `${churn}%` },
    { label: 'LTV MÉDIO', value: '—' },
  ];

  return (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 18, fontWeight: 300, color: '#F7F6F2', marginBottom: 20 }}>Financeiro</div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 16 }}>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontFamily: monoFont, fontSize: 20, fontWeight: 300, color: '#F7F6F2' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Plan breakdown */}
      <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>BREAKDOWN POR PLANO</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {planBreakdown.map(p => (
          <div key={p.plan} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontFamily: monoFont, fontSize: 13, fontWeight: 300, color: p.mrr > 0 ? '#00E87A' : 'rgba(247,246,242,0.5)', textTransform: 'uppercase' }}>{p.plan}</span>
              <span style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.5)' }}>{p.count} usuários · {formatCurrency(p.mrr)}/mês</span>
            </div>
            <div style={{ height: 4, backgroundColor: 'rgba(247,246,242,0.06)' }}>
              <div style={{ height: '100%', width: `${(p.count / maxPlanCount) * 100}%`, backgroundColor: p.mrr > 0 ? '#00E87A' : 'rgba(247,246,242,0.15)', transition: 'width 0.3s ease' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Coupons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(247,246,242,0.4)' }}>CUPONS</div>
        <button onClick={() => setShowCouponModal(true)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '6px 12px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.08)', color: '#00E87A', cursor: 'pointer' }}>+ Novo cupom</button>
      </div>

      {coupons.length === 0 ? (
        <div style={{ border: '1px dashed rgba(247,246,242,0.15)', padding: 24, textAlign: 'center' }}>
          <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)', marginBottom: 12 }}>Crie cupons para ativar leads da lista de espera e reduzir churn</div>
          <button onClick={() => setShowCouponModal(true)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '0.5px solid #00E87A', backgroundColor: 'rgba(0,232,122,0.08)', color: '#00E87A', cursor: 'pointer' }}>+ Novo cupom</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {coupons.map(c => (
            <div key={c.id} style={{ backgroundColor: 'rgba(247,246,242,0.04)', border: '0.5px solid rgba(247,246,242,0.12)', padding: 14, opacity: c.is_active ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: monoFont, fontSize: 16, fontWeight: 300, color: '#F7F6F2', letterSpacing: '0.08em' }}>{c.code}</span>
                <span style={{ fontFamily: monoFont, fontSize: 12, padding: '2px 6px', border: `0.5px solid ${c.is_active ? '#00E87A' : 'rgba(247,246,242,0.2)'}`, color: c.is_active ? '#00E87A' : 'rgba(247,246,242,0.3)' }}>
                  {c.is_active ? 'ATIVO' : 'INATIVO'}
                </span>
              </div>
              <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.45)', lineHeight: 1.8 }}>
                {c.discount_type === 'months_free' ? `${c.discount_value} ${c.discount_value === 1 ? 'mês' : 'meses'} grátis` : `${c.discount_value}% desconto`}
                {' · '}{c.applicable_plans?.join(', ').toUpperCase() || 'TODOS'}
                {' · '}Usos: {c.uses_count}/{c.max_uses || '∞'}
                {c.expires_at ? ` · Expira: ${new Date(c.expires_at).toLocaleDateString('pt-BR')}` : ''}
              </div>
              <div style={{ marginTop: 10 }}>
                <button onClick={() => setToggleTarget(c)} style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '4px 10px', border: '0.5px solid rgba(247,246,242,0.12)', backgroundColor: 'transparent', color: c.is_active ? '#FF3F2D' : '#00E87A', cursor: 'pointer' }}>
                  {c.is_active ? 'Desativar' : 'Reativar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog open={!!toggleTarget} onOpenChange={o => { if (!o) setToggleTarget(null); }} title={toggleTarget?.is_active ? 'Desativar cupom' : 'Reativar cupom'} description={`${toggleTarget?.is_active ? 'Desativar' : 'Reativar'} o cupom ${toggleTarget?.code}?`} confirmLabel={toggleTarget?.is_active ? 'Desativar' : 'Reativar'} cancelLabel="Cancelar" onConfirm={handleToggleCoupon} destructive={toggleTarget?.is_active || false} />
      {showCouponModal && <CouponModal onClose={() => setShowCouponModal(false)} onCreated={() => { setShowCouponModal(false); load(); }} />}
    </div>
  );
}
